import React, { useEffect, useState } from 'react';
import { api } from '../shared/api';
import { formatMoney } from '../shared/formatting';

type Coverage = 'live' | 'partial' | 'manual' | 'missing';

type BisAuto = {
    computed_at: string;
    lines: Array<{ line_key: string; label: string; section: string | null; amount: number | null; coverage: Coverage; note: string }>;
    bis_gap_partial: number | null;
    coverage_note: string;
    spot_eur_per_g?: number | null;
    saved?: boolean;
    history?: { computed_at: string; gap_eur: number | null }[];
};

const COVERAGE_META: Record<Coverage, { color: string; icon: string; hr: string; en: string }> = {
    live: { color: '#1f7a36', icon: '✅', hr: 'Uživo', en: 'Live' },
    partial: { color: '#b8860b', icon: '🟡', hr: 'Djelomično', en: 'Partial' },
    manual: { color: '#c2760c', icon: '🟠', hr: 'Ručno', en: 'Manual' },
    missing: { color: '#b14040', icon: '🔴', hr: 'Nedostaje', en: 'Missing' },
};

function CoverageBadge({ coverage, locale }: { coverage: Coverage; locale: 'hr' | 'en' }) {
    const meta = COVERAGE_META[coverage];
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: '0.7rem',
                fontWeight: 600,
                lineHeight: 1.4,
                color: meta.color,
                background: `${meta.color}18`,
                border: `1px solid ${meta.color}44`,
                whiteSpace: 'nowrap',
            }}
        >
            <span aria-hidden>{meta.icon}</span>
            {locale === 'en' ? meta.en : meta.hr}
        </span>
    );
}

const SECTION_LABELS: Record<string, { hr: string; en: string }> = {
    cash: { hr: 'Novac', en: 'Cash' },
    goods: { hr: 'Roba', en: 'Goods' },
    obligations: { hr: 'Obveze', en: 'Obligations' },
    result: { hr: 'Rezultat', en: 'Result' },
    memo: { hr: 'Memo', en: 'Memo' },
};

export function GapAutoModule({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const [data, setData] = useState<BisAuto | null>(null);
    const [loading, setLoading] = useState(true);
    const [computing, setComputing] = useState(false);
    const [error, setError] = useState('');

    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [inputs, setInputs] = useState<{ key: string; label: string; unit: string; value: number }[] | null>(null);
    const [showInputs, setShowInputs] = useState(false);
    const [savingInputs, setSavingInputs] = useState(false);

    const loadInputs = async () => {
        try {
            const r = await api<{ inputs: { key: string; label: string; unit: string; value: number }[] }>('/gap-report/manual-inputs', token);
            setInputs(r.inputs);
        } catch (e) { setError(e instanceof Error ? e.message : 'API'); }
    };
    const saveInputs = async () => {
        if (!inputs) return;
        setSavingInputs(true);
        try {
            const values: Record<string, number> = {};
            inputs.forEach((i) => { values[i.key] = i.value; });
            const res = await fetch('/api/gap-report/manual-inputs', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ values }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            await compute();
        } catch (e) { setError(e instanceof Error ? e.message : 'API'); }
        setSavingInputs(false);
    };

    const compute = async () => {
        setComputing(true); setError('');
        try {
            const res = await fetch('/api/gap-report/bis-auto/compute', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setData(await res.json());
        } catch (e) { setError(e instanceof Error ? e.message : 'API'); }
        setComputing(false);
    };

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError('');
        api<BisAuto>('/gap-report/bis-auto', token)
            .then((res) => {
                if (alive) {
                    setData(res);
                }
            })
            .catch((e) => {
                if (alive) {
                    setError(e instanceof Error ? e.message : 'API');
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [token]);

    const cardStyle: React.CSSProperties = {
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 12,
        padding: 16,
        background: '#fff',
    };
    const mutedLabel: React.CSSProperties = {
        color: '#8a90a6',
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    };

    if (loading) {
        return <p style={{ color: '#8a90a6' }}>{t('Učitavam…', 'Loading…')}</p>;
    }

    if (error && !data) {
        return <p style={{ color: '#b14040' }}>{error}</p>;
    }

    if (!data) {
        return <p style={{ color: '#8a90a6' }}>{t('Nema podataka.', 'No data.')}</p>;
    }

    const gap = data.bis_gap_partial;
    const gapColor = gap === null ? '#8a90a6' : gap >= 0 ? '#1f7a36' : '#b14040';
    const missingLines = data.lines.filter((l) => l.coverage !== 'live');

    // Rows grouped by section for subheaders (stable order as returned).
    const rows: Array<{ type: 'section'; label: string } | { type: 'line'; line: BisAuto['lines'][number] }> = [];
    let lastSection: string | null | undefined = undefined;
    for (const line of data.lines) {
        if (line.section !== lastSection) {
            const label =
                line.section && SECTION_LABELS[line.section]
                    ? locale === 'en'
                        ? SECTION_LABELS[line.section].en
                        : SECTION_LABELS[line.section].hr
                    : line.section ?? '';
            if (label) {
                rows.push({ type: 'section', label });
            }
            lastSection = line.section;
        }
        rows.push({ type: 'line', line });
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {error ? <p style={{ color: '#b14040', margin: 0 }}>{error}</p> : null}

            {/* 1) Top: computed_at + coverage note + IZRAČUNAJ */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 260 }}>
                    <span style={{ color: 'var(--ad-ink)', fontWeight: 600, fontSize: '0.9rem' }}>
                        {t('BIS auto-izračun', 'BIS auto-compute')} · {data.computed_at}{data.saved ? ' · 💾' : ''}
                        {data.spot_eur_per_g != null ? ` · spot ${new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 1 }).format(data.spot_eur_per_g)} €/g` : ''}
                    </span>
                    <span style={{ color: '#8a90a6', fontSize: '0.85rem' }}>{data.coverage_note}</span>
                </div>
                <button type="button" onClick={() => { setShowInputs((v) => !v); if (!inputs) void loadInputs(); }}
                    style={{ background: 'transparent', color: '#6b4f1c', border: '1px solid #d8cdb2', borderRadius: 8, padding: '9px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>
                    ⚙ {t('Ručni unosi', 'Manual inputs')}
                </button>
                <button type="button" disabled={computing} onClick={() => void compute()}
                    style={{ background: '#6b4f1c', color: '#fff', border: 0, borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>
                    {computing ? '…' : `🧮 ${t('Izračunaj i spremi', 'Compute & save')}`}
                </button>
            </div>

            {/* Ručne komponente (StoneX, blagajne, Argor, TCA, LD obveza) */}
            {showInputs ? (
                <div style={{ ...cardStyle, background: '#fbf7ee', border: '1px solid #e6d8b0' }}>
                    <div style={{ ...mutedLabel, marginBottom: 10 }}>{t('Ručne komponente GAP-a (spremi → automatski novi izračun)', 'Manual GAP components')}</div>
                    {inputs === null ? <span style={{ color: '#8a90a6' }}>{t('Učitavam…', 'Loading…')}</span> : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10 }}>
                            {inputs.map((i, idx) => (
                                <label key={i.key} style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.8rem' }}>
                                    <span style={{ color: '#6b4f1c', fontWeight: 600 }}>{i.label} <span style={{ color: '#8a90a6', fontWeight: 400 }}>({i.unit})</span></span>
                                    <input type="number" step="any" value={i.value}
                                        onChange={(e) => setInputs((p) => p!.map((x, j) => j === idx ? { ...x, value: Number(e.target.value) } : x))}
                                        style={{ padding: '6px 8px', border: '1px solid #d8cdb2', borderRadius: 6, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} />
                                </label>
                            ))}
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <button type="button" disabled={savingInputs} onClick={() => void saveInputs()}
                                    style={{ background: '#1f7a36', color: '#fff', border: 0, borderRadius: 7, padding: '8px 16px', cursor: 'pointer', fontWeight: 700 }}>
                                    {savingInputs ? '…' : t('Spremi i preračunaj', 'Save & recompute')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : null}

            {/* 1b) Povijest spremljenih izračuna */}
            {(data.history?.length ?? 0) >= 2 ? (
                <div style={{ ...cardStyle }}>
                    <div style={{ ...mutedLabel, marginBottom: 8 }}>{t('Povijest GAP izračuna', 'GAP history')} ({data.history!.length})</div>
                    {(() => {
                        const pts = data.history!.filter((h) => h.gap_eur != null) as { computed_at: string; gap_eur: number }[];
                        if (pts.length < 2) return <span style={{ color: '#8a90a6', fontSize: '0.8rem' }}>{t('Premalo točaka za graf.', 'Not enough points.')}</span>;
                        const W = 720, H = 140, PB = 18;
                        const vals = pts.map((p) => p.gap_eur);
                        const min = Math.min(...vals, 0), max = Math.max(...vals, 0), rng = max - min || 1;
                        const x = (i: number) => (i / (pts.length - 1)) * W;
                        const y = (v: number) => (H - PB) - ((v - min) / rng) * (H - PB - 8);
                        const zero = y(0);
                        return (
                            <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ overflow: 'visible' }}>
                                <line x1={0} y1={zero} x2={W} y2={zero} stroke="#ccc" strokeDasharray="4 4" />
                                <polyline points={pts.map((p, i) => `${x(i)},${y(p.gap_eur)}`).join(' ')} fill="none" stroke="#6b4f1c" strokeWidth={2} />
                                {pts.map((p, i) => (
                                    <g key={i}>
                                        <circle cx={x(i)} cy={y(p.gap_eur)} r={3} fill={p.gap_eur >= 0 ? '#1f7a36' : '#b14040'}>
                                            <title>{p.computed_at.slice(0, 16)} · {formatMoney(p.gap_eur, locale)}</title>
                                        </circle>
                                        {pts.length <= 14 || i % Math.ceil(pts.length / 10) === 0 ? (
                                            <text x={x(i)} y={H - 4} fontSize={9} fill="#8a90a6" textAnchor="middle">{p.computed_at.slice(5, 10)}</text>
                                        ) : null}
                                    </g>
                                ))}
                            </svg>
                        );
                    })()}
                </div>
            ) : null}

            {/* 2) Big number */}
            <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={mutedLabel}>
                    {t('BIS GAP (parcijalno — samo pokrivene linije)', 'BIS GAP (partial — covered lines only)')}
                </span>
                <span style={{ fontSize: '2.2rem', fontWeight: 700, color: gapColor, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                    {gap === null ? '—' : formatMoney(gap, locale)}
                </span>
            </div>

            {/* 3) Table of lines grouped by section */}
            <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ textAlign: 'left' }}>
                            {[
                                t('Linija', 'Line'),
                                t('Iznos', 'Amount'),
                                t('Pokrivenost', 'Coverage'),
                                t('Napomena', 'Note'),
                            ].map((h, i) => (
                                <th
                                    key={h}
                                    style={{
                                        padding: '10px 12px',
                                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                                        textAlign: i === 1 ? 'right' : 'left',
                                        ...mutedLabel,
                                        fontSize: '0.68rem',
                                    }}
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) =>
                            row.type === 'section' ? (
                                <tr key={`sec-${idx}`}>
                                    <td
                                        colSpan={4}
                                        style={{ padding: '10px 12px 4px', ...mutedLabel, fontSize: '0.66rem', color: 'var(--ad-acc)' }}
                                    >
                                        {row.label}
                                    </td>
                                </tr>
                            ) : (
                                <tr key={row.line.line_key}>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: 'var(--ad-ink)' }}>
                                        {row.line.label}
                                    </td>
                                    <td
                                        style={{
                                            padding: '8px 12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                                            textAlign: 'right',
                                            fontVariantNumeric: 'tabular-nums',
                                            color: row.line.amount === null ? '#8a90a6' : 'var(--ad-ink)',
                                        }}
                                    >
                                        {row.line.amount === null ? '—' : formatMoney(row.line.amount, locale)}
                                    </td>
                                    <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                        <CoverageBadge coverage={row.line.coverage} locale={locale} />
                                    </td>
                                    <td
                                        style={{
                                            padding: '8px 12px',
                                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                                            color: '#8a90a6',
                                            fontSize: '0.78rem',
                                        }}
                                    >
                                        {row.line.note}
                                    </td>
                                </tr>
                            ),
                        )}
                    </tbody>
                </table>
            </div>

            {/* 4) Checklist: what is missing for full compute */}
            <div style={cardStyle}>
                <div style={{ ...mutedLabel, marginBottom: 10 }}>{t('Što fali za pun izračun', 'Missing for full compute')}</div>
                {missingLines.length === 0 ? (
                    <p style={{ color: '#1f7a36', margin: 0, fontSize: '0.85rem' }}>
                        ✅ {t('Sve linije su pokrivene uživo.', 'All lines are covered live.')}
                    </p>
                ) : (
                    <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {missingLines.map((l) => (
                            <li key={l.line_key} style={{ fontSize: '0.85rem', color: 'var(--ad-ink)' }}>
                                <span style={{ fontWeight: 600 }}>{l.label}</span>
                                {' — '}
                                <CoverageBadge coverage={l.coverage} locale={locale} />
                                {l.note ? <span style={{ color: '#8a90a6' }}> {l.note}</span> : null}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
